// @flow
import React from 'react';
import { useParams } from 'react-router-dom';
import { getFormikFormInputsFromColConfigAndInputSource } from '@src/common-utils/utils';
import { Form } from '@src/formik-utils/formikSUIWrapper';
import { ValidateFormOnMount } from '@src/formik-utils/ValidateFormOnMount';
import FormListener from '@src/formik-utils/FormListener';
import { PromptIfFormHasUnSavedChanges } from '@presentational/PromptIfFormHasUnSavedChanges';
import { Spacer } from '@presentational/spacing/Spacer';
import { StyledContainer } from '@src/common-utils/styledComponents';
import { Formik } from 'formik';
import { TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES } from '@streaming-projects/orgs/enums';
import { useMetaDataContext } from '@streaming-projects/orgs/contexts/SPMetaDataContextProvider';
import { getMonthlyInputsValidationForType } from '@streaming-projects/workloads/cluster-workloads/monthly-inputs/validation';

import { SPTableFlowWorkloadAccordionsContainer } from './SPTableFlowWorkloadAccordionsContainer';
import { TABLEFLOW_WORKLOAD_DETAILS_CONFIG } from './config';
import { useSPTableFlowContext } from './contexts/SPTableFlowWorkloadContextProvider';
import { SPTableFlowWorkloadEnabledContainer } from './enable/SPTableFlowWorkloadEnabledContainer';
import { SPTableFlowWorkloadDeleteContainer } from './delete/SPTableFlowWorkloadDeleteContainer';

const getMaxLimitsObjectForTableFlow = (clusterType, tableFlowThresholdConfigs) => {
  const tableFlowMaxLimitsAllowed = tableFlowThresholdConfigs.tableflow_threshold_configs.find(
    (tableFlowThresholdConfig) => tableFlowThresholdConfig.cluster_type === clusterType
  );
  return {
    average_throughput_mbps: tableFlowMaxLimitsAllowed.avg_throughput_threshold,
    number_of_topics: tableFlowMaxLimitsAllowed.num_topics_threshold,
  };
};

const appendDynamicValidationsAsRequired = (
  spTableFlowWorkloadData,
  clusterResourceType,
  tableFlowConfigs
) => {
  const numberOfMonths =
    spTableFlowWorkloadData[TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.MONTHLY_INPUTS].inputs.length;

  const tableFlowMaxLimitsObject = getMaxLimitsObjectForTableFlow(
    clusterResourceType,
    tableFlowConfigs
  );

  TABLEFLOW_WORKLOAD_DETAILS_CONFIG.find(
    (x) => x.backendFieldName === TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.MONTHLY_INPUTS
  ).validation = getMonthlyInputsValidationForType(
    numberOfMonths,
    { [TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.MONTHLY_INPUTS]: tableFlowMaxLimitsObject },
    TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.MONTHLY_INPUTS
  );
};

export const SPTableFlowWorkloadTopLevelContainerForm = () => {
  const spTableFlowWorkloadData = useSPTableFlowContext();
  const { tableFlowWorkloadId } = useParams();
  const { deal_duration: dealDuration, tableflow_configs: tableflowConfigs } = useMetaDataContext();
  const retention = spTableFlowWorkloadData.inputs.retention;
  const { cluster_resource_type: clusterResourceType } =
    spTableFlowWorkloadData.tableflow_resource_details;

  const tableFlowInputsSource = {
    id: tableFlowWorkloadId,
    [TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.WORKLOAD_NAME]:
      spTableFlowWorkloadData[TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.WORKLOAD_NAME],
    [TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.IS_ENABLED]:
      spTableFlowWorkloadData[TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.IS_ENABLED],
    [TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.CLUSTER_RETENTION]:
      retention[TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.CLUSTER_RETENTION],
    [TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.CLUSTER_RETENTION_INFINITE]:
      !retention[TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.CLUSTER_RETENTION_IS_FINITE_RETENTION],
    [TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.MONTHLY_INPUTS]:
      spTableFlowWorkloadData[TABLEFLOW_WORKLOADS_INPUTS_FIELDNAMES.MONTHLY_INPUTS],
  };

  // Append the Dynamic Validations Required
  appendDynamicValidationsAsRequired(
    spTableFlowWorkloadData,
    clusterResourceType,
    tableflowConfigs
  );

  const { initialValues, initialTouched, validationSchema } =
    getFormikFormInputsFromColConfigAndInputSource(
      TABLEFLOW_WORKLOAD_DETAILS_CONFIG,
      tableFlowInputsSource,
      { dealDuration }
    );

  return (
    <StyledContainer>
      <Formik
        enableReinitialize={true}
        initialTouched={initialTouched}
        initialValues={initialValues}
        onSubmit={() => {}}
        validateOnBlur={true}
        validateOnChange={false}
        validateOnMount={true}
        validationSchema={validationSchema}
      >
        {(formik) => (
          <Form autoComplete="off">
            <ValidateFormOnMount />
            <FormListener formik={formik} />
            <PromptIfFormHasUnSavedChanges />
            <SPTableFlowWorkloadEnabledContainer
              checked={spTableFlowWorkloadData.is_enabled}
              tableFlowResourceId={spTableFlowWorkloadData?.tableflow_resource_id}
              tableFlowWorkloadId={tableFlowWorkloadId}
            />
            <SPTableFlowWorkloadDeleteContainer />
            <Spacer y={10} />
            <SPTableFlowWorkloadAccordionsContainer />
          </Form>
        )}
      </Formik>
    </StyledContainer>
  );
};
